import dayjs from 'dayjs';
import { EVENT_TYPES } from './constants';

/**
 * Group events by day for easier rendering
 * @param {Array} events - Array of event objects
 * @param {dayjs.Dayjs} weekStart - Start of the week
 * @returns {Object} Events grouped by day
 */
export const groupEventsByDay = (events, weekStart) => {
    const grouped = {};

    // Initialize all days of the week
    for (let i = 0; i < 7; i++) {
        const dayKey = weekStart.add(i, 'day').format('YYYY-MM-DD');
        grouped[dayKey] = [];
    }

    // Group events
    events.forEach(event => {
        const dayKey = dayjs(event.startTime).format('YYYY-MM-DD');
        if (grouped[dayKey]) {
            grouped[dayKey].push(event);
        }
    });

    return grouped;
};

/**
 * Determine event type for styling
 * @param {Object} event
 * @returns {string} Event type constant
 */
export const getEventType = (event) => {
    if (event.confirmed === false) return EVENT_TYPES.PENDING;
    // Check for group training - new API uses groupId/groupName, old used trainingGroup
    if (event.trainingGroup || event.groupId || event.groupName) return EVENT_TYPES.GROUP;
    if (event.isLocked) return EVENT_TYPES.LOCKED;
    if (event.confirmed === true) return EVENT_TYPES.CONFIRMED;
    return EVENT_TYPES.AVAILABLE;
};

/**
 * Ultra conservative event positioning - fixed version
 * @param {Array} events - Events for a single day
 * @returns {Array} Events with position data
 */
export const calculateEventPositions = (events) => {
    if (!events || events.length === 0) return [];

    // Sort events by start time, then by duration (longer first)
    const sortedEvents = [...events].sort((a, b) => {
        const startDiff = dayjs(a.startTime).diff(dayjs(b.startTime));
        if (startDiff !== 0) return startDiff;

        const durationA = dayjs(a.endTime).diff(dayjs(a.startTime), 'minute');
        const durationB = dayjs(b.endTime).diff(dayjs(b.startTime), 'minute');
        return durationB - durationA;
    });

    // Initialize result
    const result = sortedEvents.map(event => ({
        ...event,
        column: -1,
        totalColumns: 1
    }));

    // Simple column assignment algorithm
    for (let i = 0; i < result.length; i++) {
        const currentEvent = result[i];
        const currentStart = dayjs(currentEvent.startTime);
        const currentEnd = dayjs(currentEvent.endTime);

        // Find all events that overlap with current event
        const overlapping = [];
        for (let j = 0; j < result.length; j++) {
            if (i === j) continue;

            const otherStart = dayjs(result[j].startTime);
            const otherEnd = dayjs(result[j].endTime);

            // Check overlap
            if (currentStart.isBefore(otherEnd) && currentEnd.isAfter(otherStart)) {
                overlapping.push(j);
            }
        }

        if (overlapping.length === 0) {
            // No overlaps - full width
            result[i].column = 0;
            result[i].totalColumns = 1;
        } else {
            // Find used columns among overlapping events
            const usedColumns = new Set();
            let maxColumns = 1;

            overlapping.forEach(idx => {
                if (result[idx].column !== -1) {
                    usedColumns.add(result[idx].column);
                    maxColumns = Math.max(maxColumns, result[idx].totalColumns);
                }
            });

            // Find first available column
            let assignedColumn = 0;
            while (usedColumns.has(assignedColumn)) {
                assignedColumn++;
            }

            // Update total columns for all overlapping events
            const newTotalColumns = Math.max(maxColumns, assignedColumn + 1);

            // Set column for current event
            result[i].column = assignedColumn;
            result[i].totalColumns = newTotalColumns;

            // Update total columns for all overlapping events that were already processed
            overlapping.forEach(idx => {
                if (result[idx].column !== -1) {
                    result[idx].totalColumns = newTotalColumns;
                }
            });
        }
    }

    // Second pass to update any events that might have been missed
    for (let i = 0; i < result.length; i++) {
        const currentEvent = result[i];
        const currentStart = dayjs(currentEvent.startTime);
        const currentEnd = dayjs(currentEvent.endTime);

        // Find max columns among overlapping events
        let maxColumns = currentEvent.totalColumns;

        for (let j = 0; j < result.length; j++) {
            if (i === j) continue;

            const otherStart = dayjs(result[j].startTime);
            const otherEnd = dayjs(result[j].endTime);

            // Check overlap
            if (currentStart.isBefore(otherEnd) && currentEnd.isAfter(otherStart)) {
                maxColumns = Math.max(maxColumns, result[j].totalColumns);
            }
        }

        result[i].totalColumns = maxColumns;
    }

    // Debug log
    console.log('📊 Event positioning complete:');
    result.forEach(event => {
        console.log(`${dayjs(event.startTime).format('HH:mm')}-${dayjs(event.endTime).format('HH:mm')}: Column ${event.column}/${event.totalColumns}`);
    });

    return result;
};

/**
 * Format time for display based on user preferences
 * @param {dayjs.Dayjs} time
 * @param {boolean} use24Hour
 * @returns {string} Formatted time
 */
export const formatTimeDisplay = (time, use24Hour = true) => {
    return use24Hour ? time.format('HH:mm') : time.format('h:mm A');
};

/**
 * Get week dates for header display
 * @param {dayjs.Dayjs} weekStart
 * @returns {Array} Array of date objects for the week
 */
export const getWeekDates = (weekStart) => {
    return Array.from({ length: 7 }, (_, i) => {
        const date = weekStart.add(i, 'day');
        return {
            date,
            dayName: date.format('ddd'),
            dayNumber: date.format('D'),
            monthName: date.format('MMM'),
            isToday: date.isSame(dayjs(), 'day'),
            isWeekend: date.day() === 0 || date.day() === 6,
        };
    });
};

/**
 * Check if a trainer is available at a specific time
 * @param {dayjs.Dayjs} startTime
 * @param {dayjs.Dayjs} endTime
 * @param {Array} existingEvents
 * @param {Array} lockedSlots
 * @returns {boolean}
 */
export const isTrainerAvailable = (startTime, endTime, existingEvents = [], lockedSlots = []) => {
    // Check existing events
    const hasConflict = existingEvents.some(event => {
        const eventStart = dayjs(event.startTime);
        const eventEnd = dayjs(event.endTime);

        return startTime.isBefore(eventEnd) && endTime.isAfter(eventStart);
    });

    if (hasConflict) return false;

    // Check locked slots
    const isLocked = lockedSlots.some(lock => {
        const lockStart = dayjs(lock.startTime);
        const lockEnd = dayjs(lock.endTime);

        return startTime.isBefore(lockEnd) && endTime.isAfter(lockStart);
    });

    return !isLocked;
};

/**
 * Generate time slots for the time column
 * @param {string} startTime - "HH:mm"
 * @param {string} endTime - "HH:mm"
 * @param {number} intervalMinutes - Interval between slots
 * @returns {Array} Time slot strings
 */
export const generateTimeSlots = (startTime, endTime, intervalMinutes = 60) => {
    const slots = [];
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    let currentHour = startHour;
    let currentMinute = startMinute;

    while (currentHour < endHour || (currentHour === endHour && currentMinute <= endMinute)) {
        slots.push(`${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`);

        currentMinute += intervalMinutes;
        if (currentMinute >= 60) {
            currentHour += Math.floor(currentMinute / 60);
            currentMinute = currentMinute % 60;
        }
    }

    return slots;
};

/**
 * Get color scheme for event based on type
 * @param {string} eventType
 * @returns {Object} Colors for background, border, text
 */
export const getEventColors = (eventType) => {
    const colorSchemes = {
        [EVENT_TYPES.CONFIRMED]: {
            background: '#DCF538',
            border: '#B8D919',
            text: '#15803D',
        },
        [EVENT_TYPES.PENDING]: {
            background: '#F2A516',
            border: '#D08910',
            text: '#FFFFFF',
        },
        [EVENT_TYPES.GROUP]: {
            background: '#FCD34D',
            border: '#F59E0B',
            text: '#78350F',
        },
        [EVENT_TYPES.LOCKED]: {
            background: '#6B7280',
            border: '#4B5563',
            text: '#FFFFFF',
        },
        [EVENT_TYPES.AVAILABLE]: {
            background: '#ACA6FF',
            border: '#928FEE',
            text: '#FFFFFF',
        },
    };

    return colorSchemes[eventType] || colorSchemes[EVENT_TYPES.AVAILABLE];
};

/**
 * Filter events within a date range
 * @param {Array} events
 * @param {dayjs.Dayjs} startDate
 * @param {dayjs.Dayjs} endDate
 * @returns {Array} Filtered events
 */
export const filterEventsByDateRange = (events, startDate, endDate) => {
    return events.filter(event => {
        const eventStart = dayjs(event.startTime);
        const eventEnd = dayjs(event.endTime);

        // Event overlaps with the date range
        return eventStart.isBefore(endDate) && eventEnd.isAfter(startDate);
    });
};

/**
 * Merge different types of calendar items (trainings, locks, groups)
 * @param {Object} data - Contains confirmedTrainings, pendingBookings, groupTrainings, lockedSlots
 * @returns {Array} Unified event array
 */
export const mergeCalendarEvents = (data) => {
    const events = [];

    // Add confirmed trainings
    if (data.confirmedTrainings) {
        events.push(...data.confirmedTrainings.map(training => ({
            ...training,
            type: EVENT_TYPES.CONFIRMED,
        })));
    }

    // Add group trainings
    if (data.groupTrainings) {
        events.push(...data.groupTrainings.map(training => ({
            ...training,
            type: EVENT_TYPES.GROUP,
        })));
    }

    // Add locked slots
    if (data.lockedSlots) {
        events.push(...data.lockedSlots.map(lock => ({
            ...lock,
            type: EVENT_TYPES.LOCKED,
            isLocked: true,
        })));
    }

    return events;
};